import { Close, Visibility } from "@mui/icons-material";
import { alpha, Box, Button, styled, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";

import { UserRoleSelector } from "@components/UserRoleSelector/UserRoleSelector";

type Props = {
  // Target user id; null is the current user.
  value: string | null;
  onChange: (userId: string | null) => void;
  isViewingOther: boolean;
  // Display name of the user being viewed, used only for the screen-reader announcement.
  effectiveUserName?: string | null;
  // Rows the backend withheld from the listing because the *viewer's* rules
  // don't cover them. An explicit 0 means nothing is hidden, so no note is
  // shown; absent (older backend) falls back to a generic partial-view note.
  hiddenPoliciesCount?: number;
  // True while the policy listing is in flight, when the count isn't known yet.
  // Without this the generic fallback note would flash on every refetch —
  // paging, sorting, filtering — before resolving to the counted wording.
  isPoliciesFetching?: boolean;
};

// Full-bleed strip above the page title: neutral for the current user, primary
// colour in view-as mode. Negative margins cancel the page container's padding.
const StripRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>(({ theme, active }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 4, 1, 6),
  marginTop: theme.spacing(-4),
  marginLeft: theme.spacing(-6),
  marginRight: theme.spacing(-4),
  marginBottom: theme.spacing(2),
  backgroundColor: active
    ? theme.palette.primary.main
    : // Matches the table column-header grey (DataTable StyledHeaderCell).
      theme.palette.grey[200],
  color: active
    ? theme.palette.primary.contrastText
    : theme.palette.text.secondary,
  borderBottom: `1px solid ${active ? "transparent" : theme.palette.divider}`,
}));

const EyeIcon = styled(Visibility)({
  flex: "none",
});

const ModeTag = styled("span")(({ theme }) => ({
  fontSize: "0.65rem",
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  padding: theme.spacing(0.25, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.primary.contrastText, 0.22),
}));

const ViewingAsLabel = styled(Typography)(({ theme }) => ({
  marginRight: theme.spacing(0.5),
}));

const Spacer = styled("div")({
  flexGrow: 1,
});

// Partial-view note, shown only in view-as mode: the listing is filtered by
// the viewer's own permissions, so it may not be the viewed user's full access.
const VisibilityNote = styled(Typography)({
  flex: "none",
  fontStyle: "italic",
});

const ExitButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  borderColor: alpha(theme.palette.primary.contrastText, 0.6),
  "&:hover": {
    borderColor: theme.palette.primary.contrastText,
    backgroundColor: alpha(theme.palette.primary.contrastText, 0.12),
  },
}));

const ViewAsBanner = ({
  value,
  onChange,
  isViewingOther,
  effectiveUserName,
  hiddenPoliciesCount,
  isPoliciesFetching = false,
}: Props) => {
  return (
    <StripRoot active={isViewingOther}>
      {/* Announce entering view-as mode (and switching between users) to screen
          readers. Kept as a dedicated visually-hidden node rather than a
          role="status" on the whole strip: a live region wrapping the picker
          would re-announce on every keystroke and loading-spinner toggle. */}
      <Box component="span" role="status" sx={visuallyHidden}>
        {isViewingOther
          ? `Now viewing permissions as ${effectiveUserName ?? "another user"}`
          : ""}
      </Box>
      <EyeIcon fontSize="small" />
      {isViewingOther ? (
        <ModeTag>View-as mode</ModeTag>
      ) : (
        <ViewingAsLabel variant="body2">Viewing as</ViewingAsLabel>
      )}
      <UserRoleSelector
        usersOnly
        label=""
        ariaLabel="View as user"
        placeholder={
          isViewingOther ? "" : "Me - click to view as another user…"
        }
        value={value}
        onChange={onChange}
        sx={{
          width: (theme) => theme.spacing(40),
          "& .MuiInputBase-root": {
            backgroundColor: (theme) => theme.palette.background.paper,
            borderRadius: (theme) => `${theme.shape.borderRadius}px`,
          },
        }}
      />
      {isViewingOther && !isPoliciesFetching && hiddenPoliciesCount !== 0 && (
        <VisibilityNote variant="caption">
          {hiddenPoliciesCount
            ? `${hiddenPoliciesCount} ${
                hiddenPoliciesCount === 1 ? "policy is" : "policies are"
              } hidden — your own permissions don't cover them`
            : "You're only seeing the policies your own permissions allow you to view"}
        </VisibilityNote>
      )}
      <Spacer />
      {isViewingOther && (
        <ExitButton
          size="small"
          variant="outlined"
          startIcon={<Close />}
          onClick={() => onChange(null)}
        >
          View as me
        </ExitButton>
      )}
    </StripRoot>
  );
};

export default ViewAsBanner;
